import * as React from 'react';

import type { ListCriteriaNode } from '../../../api/lists-contracts';
import {
  newGroup,
  readCriteria,
  replaceNode,
  withoutNodes,
  writeCriteria,
  type CriteriaNode,
  type ICriteriaGroup,
  type KindOf
} from '../../../list-criteria';
import type { IStateSignal } from '../../../components/state-cards';
import { filterParam, type IWorkPage } from './use-work-page';

/**
 * A list-backed work page's card signals as filters (SR-485) — DD calls, Reinstatement, Metrics
 * updates: the signals pressed are one `or` group at the root of the page's Lists filter, a branch
 * each — the leaves its count reads, as an `and` group — ANDed with the rest, so the rows read it
 * like any other condition, *Advanced…* shows it and *Open in Lists* carries it. A root `or` child
 * whose every branch is spelled as one of the page's signals is that group: said in the chips line
 * by its signals' names (`SignalChips`), not as "n conditions"; a signal pressed again, or its ✕,
 * removes its branch, and the last one the group. The cards count without it (`useCountedFilter`):
 * a signal narrows the rows, not the numbers it is pressed from.
 */
export interface IWorkSignal {
  key: string;
  /** The chip's words — the signal's label, "overdue". */
  words: string;
  leaves: readonly ListCriteriaNode[];
}

/** The page's tree and how its fields are spelled — what the signals are found in. */
type Tree = Pick<IWorkPage, 'root' | 'kindOf'>;

/** A signal's branch as the builder holds it, under fresh ids. */
function groupOf(leaves: readonly ListCriteriaNode[]): CriteriaNode {
  return readCriteria({ version: 1, op: 'and', children: [{ op: 'and', children: [...leaves] }] }).children[0];
}

/** A node as the wire spells it, to compare with a signal's branch. */
function spelled(node: CriteriaNode, kindOf: KindOf): string {
  return JSON.stringify(writeCriteria({ id: '', type: 'group', op: 'and', children: [node] }, kindOf).children[0]);
}

/** The signals by the spelling of their branch. */
function bySpelling(signals: readonly IWorkSignal[], kindOf: KindOf): Map<string, IWorkSignal> {
  return new Map(signals.map((signal) => [spelled(groupOf(signal.leaves), kindOf), signal]));
}

/** The group whose every branch is one of `known`, null while none is on. */
function groupIn({ root, kindOf }: Tree, known: ReadonlyMap<string, IWorkSignal>): Nullable<ICriteriaGroup> {
  if (root.op !== 'and') return null;
  return (
    root.children.find(
      (child): child is ICriteriaGroup =>
        child.type === 'group' &&
        child.op === 'or' &&
        child.children.length > 0 &&
        child.children.every((branch) => known.has(spelled(branch, kindOf)))
    ) ?? null
  );
}

/** The signals' `or` group at the root, null while none is on. */
export function signalGroupOf(tree: Tree, signals: readonly IWorkSignal[]): Nullable<ICriteriaGroup> {
  return groupIn(tree, bySpelling(signals, tree.kindOf));
}

/** The root with the signal's branch added, or removed — the group with its last; an `or` root
 *  kept whole as one child. */
export function withSignal(
  { root, kindOf }: Tree,
  signals: readonly IWorkSignal[],
  leaves: readonly ListCriteriaNode[],
  on: boolean
): ICriteriaGroup {
  const group = groupIn({ root, kindOf }, bySpelling(signals, kindOf));
  const wanted = spelled(groupOf(leaves), kindOf);
  const branch = group?.children.find((child) => spelled(child, kindOf) === wanted);
  if (!on) {
    if (!group || !branch) return root;
    return withoutNodes(root, [group.children.length === 1 ? group.id : branch.id]);
  }
  if (branch) return root;
  if (group) return replaceNode(root, group.id, { ...group, children: [...group.children, groupOf(leaves)] });
  const base =
    root.op === 'and' || root.children.length === 0
      ? { ...root, op: 'and' as const }
      : readCriteria({ version: 1, op: 'and', children: [{ op: root.op, children: writeCriteria(root, kindOf).children }] });
  return { ...base, children: [...base.children, newGroup('or', [groupOf(leaves)])] };
}

/** The signals on, in the order they were pressed — the chips line's. */
export function signalsOn(tree: Tree, signals: readonly IWorkSignal[]): IWorkSignal[] {
  const known = bySpelling(signals, tree.kindOf);
  return (groupIn(tree, known)?.children ?? []).flatMap((branch) => known.get(spelled(branch, tree.kindOf)) ?? []);
}

/** The page's filter as the cards count under it: the signals' group left out — the user's own
 *  conditions, not the signals pressed. One node while its spelling stays, so a press — which
 *  moves only the group — reads no count again. */
export function useCountedFilter(page: IWorkPage, signals: readonly IWorkSignal[]): Nullable<ListCriteriaNode> {
  const group = signalGroupOf(page, signals);
  const rest = group ? withoutNodes(page.root, [group.id]) : page.root;
  const written = rest.children.length > 0 ? writeCriteria(rest, page.kindOf) : null;
  const spelling = written ? JSON.stringify({ op: written.op, children: written.children }) : null;
  return React.useMemo(() => (spelling ? (JSON.parse(spelling) as ListCriteriaNode) : null), [spelling]);
}

/**
 * A card's signals as toggles: one is active where its card is open and its branch is on; a press on
 * the open card adds or removes it, on another opens that card (`openCard`, which writes the page's
 * own parameter into the address) with it added — one write, the filter's `f` and the card's
 * together.
 */
export function toggledSignals({
  page,
  params,
  setParams,
  current,
  signals,
  specs,
  pressed,
  openCard,
  nameOf
}: {
  page: IWorkPage;
  params: URLSearchParams;
  setParams: (next: URLSearchParams) => void;
  /** Whether the signals' card is the open one. */
  current: boolean;
  signals: readonly IStateSignal[] | undefined;
  /** Every signal the page's group may hold, each found by its key — a badge none names stays a
   *  plain badge. */
  specs: readonly IWorkSignal[];
  /** The keys of the signals on (`signalsOn`). */
  pressed: readonly string[];
  openCard: (next: URLSearchParams) => void;
  /** The toggle's accessible name. */
  nameOf: (signal: IStateSignal) => string;
}): IStateSignal[] | undefined {
  return signals?.map((signal) => {
    const spec = specs.find((one) => one.key === signal.key);
    if (!spec) return signal;
    const active = current && pressed.includes(spec.key);
    return {
      ...signal,
      active,
      toggleName: nameOf(signal),
      onToggle: () => {
        const next = new URLSearchParams(params);
        if (!current) openCard(next);
        const f = filterParam(withSignal(page, specs, spec.leaves, !active), page.kindOf);
        if (f === null) next.delete('f');
        else next.set('f', f);
        setParams(next);
      }
    };
  });
}
